import { useMemo } from 'react'
import { uniqueOptions } from 'Utils/profileAccess.utils'

// ─────────────────────────────────────────────────────────────────────────────
// OPTION BUILDERS
// Pure functions — no hooks, safe to call inside or outside React
// ─────────────────────────────────────────────────────────────────────────────

// job_family_name is not on the requisition object directly;
// it lives in orgMetaData.jobs keyed by job_code — Map for O(1) lookup
export const buildRoleIdOptions = (requisitionViewData = [], orgMetaData) => {
  const jobsMap = new Map((orgMetaData?.jobs ?? []).map((j) => [j.job_code, j]))

  return requisitionViewData.map((req) => {
    const job = jobsMap.get(req.job_code)
    const label = job?.job_family_name || req?.job_title || req?.job_title_for_jd || ''
    return {
      value: req.sb_requisition_number,
      label: `${req.sb_requisition_number} - ${label}`,
    }
  })
}

// All legal entities come from the top level of org structure
export const buildLegalEntityOptions = (structure = []) =>
  dropRedundantAll(structure.map((item) => ({ value: item.legal_entity, label: item.legal_entity })))

// ─────────────────────────────────────────────────────────────────────────────
// CASCADING OPTIONS  (Legal → BU → Function → SubFunction)
// ─────────────────────────────────────────────────────────────────────────────

// "ALL" is only meaningful when it stands for more than one thing. With a single
// real option the sentinel is a duplicate of it, so hide it. With zero real
// options the sentinel is the only selectable value — keep it.
const dropRedundantAll = (options = []) => {
  const realOptions = options.filter((o) => !isSelectAll(o.value))
  return realOptions.length === 1 ? realOptions : options
}

export const getChainedOptions = (structure = [], { legalEntities, businessUnits, functions }) => {
  const filteredLegal = structure.filter((item) => legalEntities.includes(item.legal_entity))

  const businessUnitOptions = dropRedundantAll(uniqueOptions(
    filteredLegal.flatMap((item) =>
      item.business_units.map((bu) => ({ value: bu.business_unit, label: bu.business_unit }))
    )
  ))

  const selectedBUObjects = filteredLegal
    .flatMap((item) => item.business_units)
    .filter((bu) => businessUnits.includes(bu.business_unit))

  const functionOptions = dropRedundantAll(uniqueOptions(
    selectedBUObjects.flatMap((bu) =>
      bu.functions.map((func) => ({ value: func.function, label: func.function }))
    )
  ))

  const selectedFunctionObjects = selectedBUObjects
    .flatMap((bu) => bu.functions)
    .filter((func) => functions.includes(func.function))

  const subFunctionOptions = dropRedundantAll(uniqueOptions(
    selectedFunctionObjects.flatMap((func) =>
      func.sub_functions.map((sub) => ({ value: sub.sub_function, label: sub.sub_function }))
    )
  ))

  return { businessUnitOptions, functionOptions, subFunctionOptions }
}

// ─────────────────────────────────────────────────────────────────────────────
// SELECTION HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Returns true for sentinel values like "AL - All", "AL-ALL", "all"
export const isSelectAll = (value) =>
  typeof value === 'string' &&
  value.split(/\s*[-_]\s*/).pop().trim().toLowerCase() === 'all'

// Drops values no longer present in valid options after a parent-level change
export const filterSelected = (selected, validOptions) => {
  const validSet = new Set(validOptions.map((o) => o.value))
  return (Array.isArray(selected) ? selected : []).filter((v) => validSet.has(v))
}

// Full Org — returns every real org value as arrays, never a sentinel string
export const getAllOrgValues = (structure = []) => {
  const legalEntities = structure.map((item) => item.legal_entity)

  const businessUnits = uniqueOptions(
    structure.flatMap((item) =>
      item.business_units.map((bu) => ({ value: bu.business_unit, label: bu.business_unit }))
    )
  ).map((o) => o.value)

  const functions = uniqueOptions(
    structure.flatMap((item) =>
      item.business_units.flatMap((bu) =>
        bu.functions.map((func) => ({ value: func.function, label: func.function }))
      )
    )
  ).map((o) => o.value).filter((v) => !isSelectAll(v))

  const subFunctions = uniqueOptions(
    structure.flatMap((item) =>
      item.business_units.flatMap((bu) =>
        bu.functions.flatMap((func) =>
          func.sub_functions.map((sub) => ({ value: sub.sub_function, label: sub.sub_function }))
        )
      )
    )
  ).map((o) => o.value).filter((v) => !isSelectAll(v))

  return {
    legal_entities: legalEntities,
    business_units: businessUnits,
    functions,
    sub_functions: subFunctions,
  }
}

// Replaces sentinel values (e.g. "AL - All") with all real options from the list —
// used before cascade drilling and at payload build time.
// If the only available option is the sentinel itself (e.g. a BU that has no real
// functions), keep the sentinel so the selection isn't silently dropped.
export const expandSentinels = (values = [], options = []) => {
  if (!values.some(isSelectAll)) return values
  const realValues = options.filter((o) => !isSelectAll(o.value)).map((o) => o.value)
  if (realValues.length) return realValues
  return values.filter(isSelectAll).slice(0, 1)
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────────────────

export const useRoleIdOptions = (requisitionViewData, orgMetaData) =>
  useMemo(
    () => buildRoleIdOptions(requisitionViewData, orgMetaData),
    [requisitionViewData, orgMetaData]
  )
